import { lowerFirst } from "lodash-es"

import { AssessmentBand } from "@/constants/terminal/assessment-band"
import type { DetectionClass } from "@/constants/terminal/detection-class"
import type { DetectionLifecycleState } from "@/constants/terminal/detection-lifecycle-state"
import { ReviewMeasurementKey } from "@/constants/terminal/review-measurement-key"
import { ScoreInputKind } from "@/constants/terminal/score-input-kind"
import {
  dataByUnclearReason,
  type UnclearReason,
} from "@/constants/terminal/unclear-reason"
import { detectionSentenceLabel } from "@/lib/terminal/detection-type-label"
import { detectionIdentity } from "@/terminal/file/assessment-value-schemas"

// What the Digest reads of a Company's File at one time: its current
// Assessment, the points of each Score Breakdown row, its Detection Board,
// Scope and Brief as of then, plus the Assessment's policy hash and commit.
export type DigestFile = {
  assessmentId: string
  companyId: string
  companyName: string
  score: number | null
  band: AssessmentBand
  unclearReason: UnclearReason | null
  scoreRows: ScoreRow[]
  briefText: string
  scopeEntryIds: string[]
  // The identity, detections and assessment policy versions the Assessment
  // ran under: a writing change rewords a File without changing its method.
  methodologyVersions: string
  codeCommitSha: string | null
  detections: DigestDetection[]
}

type DigestDetection = {
  detectionTypeKey: string
  detectionKey: string
  label: string
  class: DetectionClass
  lifecycleState: DetectionLifecycleState
  materialMeaning: string
  title: string
}

export type FilePair = { before: DigestFile; after: DigestFile }

// The two lists joined on a key: each key with its item on either side.
const outerJoin = <T>(
  beforeItems: T[],
  afterItems: T[],
  keyOf: (item: T) => string
) => {
  const beforeByKey = new Map(beforeItems.map((item) => [keyOf(item), item]))
  const afterByKey = new Map(afterItems.map((item) => [keyOf(item), item]))
  return [...new Set([...beforeByKey.keys(), ...afterByKey.keys()])].map(
    (key) => ({ was: beforeByKey.get(key), now: afterByKey.get(key) })
  )
}

// What a move reads of one Score Breakdown row. A Detection's row names its
// durable id; a simulated or rescored row has none.
export type ScoreRow = {
  scoreInputKey: string
  // The Policy Tuner's engine names a kind by its value.
  kind: `${ScoreInputKind}`
  label: string
  pointAdjustment: number
  detectionId?: string | null
}

type DriverFile = Pick<DigestFile, "band" | "unclearReason" | "scoreRows">

// One Score Input that moved the score, the way it pushed it. A Detection's
// row names its durable id, which the screens open; any other row, or a
// Detection's row without an id, is a plain sentence.
export type ScoreDriver = {
  direction: "up" | "down"
  detectionId: string | null
  // How a Detection's row moved: "new" when it entered the score, "aging"
  // when it stayed but its deduction shrank, which raises the score by age
  // (ADR-0129, Detections age in a straight line), not by going away.
  detectionChange: DetectionChange | null
  text: string
}

export type DetectionChange = "new" | "aging"

// What an aging Detection's line adds, on screen and in the email.
export const AGING_NOTE = "weighs less with age"

// A row whose change rounds to no point is aging, not a cause.
const MINIMUM_DRIVER_POINTS = 0.5

const CLEAN_YEAR_KEY = "assessment_clean_year_credit"

const driverText = (
  { scoreInputKey, kind, label }: ScoreRow,
  direction: ScoreDriver["direction"],
  hasLeft: boolean
) => {
  if (kind === ScoreInputKind.DETECTION)
    return detectionSentenceLabel({
      typeKey: scoreInputKey.split(":")[0],
      typeLabel: label,
    })
  // Both scorers' labels carry the rate as the first percentage.
  if (scoreInputKey === ReviewMeasurementKey.CUSTOMER_HARM_RATE)
    return `Customer harm rate ${direction === "down" ? "rose" : "fell"} to ${/\d+(\.\d+)?%/.exec(label)?.[0]}`
  // A File earns no clean year and so carries no row: the line counts the
  // new File's zero, not the old File's years.
  if (scoreInputKey === CLEAN_YEAR_KEY && hasLeft)
    return `${label.split(":")[0]}: 0`
  // An aggregate's label leads with what it counts, before its detail.
  return label.split(",")[0]
}

const detectionChangeOf = (
  was: ScoreRow | undefined,
  now: ScoreRow | undefined
): DetectionChange | null => {
  if (now?.kind !== ScoreInputKind.DETECTION) return null
  if (was === undefined) return "new"
  return was.pointAdjustment < now.pointAdjustment && now.pointAdjustment < 0
    ? "aging"
    : null
}

// A line's text as the email prints it; the screen builds the same words
// around the Detection's link.
const detectionChangeText: Record<
  DetectionChange | "none",
  (text: string) => string
> = {
  new: (text) => `New ${lowerFirst(text)}`,
  aging: (text) => `${text} ${AGING_NOTE}`,
  none: (text) => text,
}

// Policy limits only slow a move, so none is a cause.
const isCause = ({ kind }: ScoreRow) =>
  kind !== ScoreInputKind.POLICY_CONSTRAINT

// The Score Inputs behind a move from `before` to `after`, largest first: each
// Detection or aggregate row whose points moved, or the one that moved most
// when none moved a whole point. A move into or out of UNCLEAR is its reason
// alone.
export const scoreDrivers = (
  before: DriverFile,
  after: DriverFile
): ScoreDriver[] => {
  if (after.band === AssessmentBand.UNCLEAR && after.unclearReason)
    return [
      {
        direction: "down",
        detectionId: null,
        detectionChange: null,
        text: dataByUnclearReason[after.unclearReason].label,
      },
    ]
  if (before.band === AssessmentBand.UNCLEAR)
    return [
      {
        direction: "up",
        detectionId: null,
        detectionChange: null,
        text: "Graded again",
      },
    ]
  const moves = outerJoin(
    before.scoreRows.filter(isCause),
    after.scoreRows.filter(isCause),
    (row) => row.scoreInputKey
  )
    .map(({ was, now }) => ({
      row: (now ?? was)!,
      points: (now?.pointAdjustment ?? 0) - (was?.pointAdjustment ?? 0),
      hasLeft: now === undefined,
      detectionChange: detectionChangeOf(was, now),
    }))
    .filter(({ points }) => points !== 0)
    .sort((left, right) => Math.abs(right.points) - Math.abs(left.points))
  const causes = moves.filter(
    ({ points }) => Math.abs(points) >= MINIMUM_DRIVER_POINTS
  )
  return (causes.length > 0 ? causes : moves.slice(0, 1)).map(
    ({ row, points, hasLeft, detectionChange }) => {
      const direction = points > 0 ? "up" : "down"
      const text = driverText(row, direction, hasLeft)
      return {
        direction,
        detectionId:
          row.kind === ScoreInputKind.DETECTION
            ? (row.detectionId ?? null)
            : null,
        detectionChange,
        text: detectionChangeText[detectionChange ?? "none"](text),
      }
    }
  )
}

// Score Inputs joined on their key: what entered, left, or moved the score.
// Policy constraint rows carry no points, so a limit shows as its own row.
const scoreInputChanges = (before: DigestFile, after: DigestFile) =>
  outerJoin(before.scoreRows, after.scoreRows, (row) => row.scoreInputKey)
    .filter(({ was, now }) => was?.pointAdjustment !== now?.pointAdjustment)
    .map(({ was, now }) => ({
      scoreInputKey: (now ?? was)!.scoreInputKey,
      label: (now ?? was)!.label,
      pointsBefore: was?.pointAdjustment ?? null,
      pointsAfter: now?.pointAdjustment ?? null,
    }))

const detectionChangeKind = (was?: DigestDetection, now?: DigestDetection) => {
  if (was === undefined) return "added" as const
  if (now === undefined) return "removed" as const
  return "changed" as const
}

// Detections joined on their Detection Key across the two times.
const detectionChanges = (before: DigestFile, after: DigestFile) =>
  outerJoin(before.detections, after.detections, detectionIdentity)
    .filter(
      ({ was, now }) =>
        was?.lifecycleState !== now?.lifecycleState ||
        was?.materialMeaning !== now?.materialMeaning
    )
    .map(({ was, now }) => {
      const detection = (now ?? was)!
      return {
        detectionTypeKey: detection.detectionTypeKey,
        label: detection.label,
        class: detection.class,
        title: detection.title,
        change: detectionChangeKind(was, now),
        lifecycleBefore: was?.lifecycleState ?? null,
        lifecycleAfter: now?.lifecycleState ?? null,
        meaningBefore: was?.materialMeaning ?? null,
        meaningAfter: now?.materialMeaning ?? null,
      }
    })

const countMissing = (ids: string[], from: string[]) => {
  const present = new Set(from)
  return ids.filter((id) => !present.has(id)).length
}

// A pair's move: its points between two graded Assessments, and whether its
// Assessment Band changed, as a move into or out of UNCLEAR always does. It
// reads only the two Assessments, so the customer Terminal's movement marks
// apply the same rule.
type AssessmentPair = Record<keyof FilePair, Pick<DigestFile, "score" | "band">>

export const digestMove = ({ before, after }: AssessmentPair) => ({
  delta:
    before.score !== null && after.score !== null
      ? after.score - before.score
      : null,
  isBandMove: before.band !== after.band,
})

export type DigestMove = ReturnType<typeof digestMove>

const toFileChange = ({ before, after }: FilePair) => ({
  companyId: after.companyId,
  companyName: after.companyName,
  beforeAssessmentId: before.assessmentId,
  afterAssessmentId: after.assessmentId,
  scoreBefore: before.score,
  scoreAfter: after.score,
  delta: digestMove({ before, after }).delta,
  bandBefore: before.band,
  bandAfter: after.band,
  unclearReasonBefore: before.unclearReason,
  unclearReasonAfter: after.unclearReason,
  scoreInputs: scoreInputChanges(before, after),
  drivers: scoreDrivers(before, after),
  detections: detectionChanges(before, after),
  scopeEntriesAdded: countMissing(after.scopeEntryIds, before.scopeEntryIds),
  scopeEntriesRemoved: countMissing(before.scopeEntryIds, after.scopeEntryIds),
  isMethodologyChanged:
    before.methodologyVersions !== after.methodologyVersions ||
    before.codeCommitSha !== after.codeCommitSha,
  briefBefore: before.briefText,
  briefAfter: after.briefText,
})

export type FileChange = ReturnType<typeof toFileChange>

// Scored changes by delta, largest loss first; then gradability changes,
// losses of gradability first.
const changeOrder = (left: FileChange, right: FileChange) => {
  if (left.delta !== null && right.delta !== null)
    return (
      left.delta - right.delta ||
      left.companyName.localeCompare(right.companyName)
    )
  if (left.delta !== null) return -1
  if (right.delta !== null) return 1
  const isLeftLoss = left.bandAfter === AssessmentBand.UNCLEAR
  const isRightLoss = right.bandAfter === AssessmentBand.UNCLEAR
  if (isLeftLoss !== isRightLoss) return isLeftLoss ? -1 : 1
  return left.companyName.localeCompare(right.companyName)
}

// The Digest's default threshold, in Assessment Score points.
export const DIGEST_SCORE_THRESHOLD = 2

// A move to another Assessment Band, UNCLEAR included, however few points
// (owner ruling 2026-10-21), or a move of `threshold` points or more within
// one band (owner ruling 2026-10-13, from the Digest backtest).
export const isDigestMove = (
  { delta, isBandMove }: DigestMove,
  threshold: number
) => isBandMove || (delta !== null && Math.abs(delta) >= threshold)

// The Digest's File Changes among the window's start-and-end pairs, in
// the order the email lists them.
export const fileChanges = (pairs: FilePair[], threshold: number) =>
  pairs
    .filter((pair) => isDigestMove(digestMove(pair), threshold))
    .map(toFileChange)
    .sort(changeOrder)
